import { useState, useEffect, useRef, useCallback  } from "react";
import { syncPendingOperations } from "../utils/syncQueue";
import { getQueueCount } from "../utils/offlineQueue";
import toast from "react-hot-toast";

export default function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const syncedRef = useRef(false);

  const refreshCount = async () => {
    try {
      const count = await getQueueCount();
      setPendingCount(count);
      return count;
    } catch {
      return 0;
    }
  };

  const runSync = useCallback(async () => {
  if (syncedRef.current) return;
  syncedRef.current = true;

  const count = await refreshCount();
  if (count === 0) {
    syncedRef.current = false;
    return;
  }

  setSyncing(true);

  try {
    const result = await syncPendingOperations();
    await refreshCount();

    if (result.success > 0) {
      toast.success(
        `Synced ${result.success} offline record${result.success > 1 ? "s" : ""} successfully`
      );

      window.dispatchEvent(new Event("martsense-sync-complete"));

      setJustSynced(true);

      setTimeout(() => {
        setJustSynced(false);
        setPendingCount(0);
      }, 3000);
    }

    if (result.failed > 0) {
      toast.error(
        `${result.failed} record${result.failed > 1 ? "s" : ""} failed to sync`
      );
    }
  } catch {
    toast.error("Sync failed. Will retry when connection is stable.");
  } finally {
    setSyncing(false);
    syncedRef.current = false;
  }
}, []);

  useEffect(() => {
  refreshCount();

  const handleOnline = async () => {
    setIsOnline(true);
    await runSync();
  };

  const handleOffline = () => {
    setIsOnline(false);
    refreshCount();
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  const interval = setInterval(refreshCount, 5000);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
    clearInterval(interval);
  };
}, [runSync]);

  // Hide banner when online with nothing pending and not syncing
  if (isOnline && pendingCount === 0 && !syncing && !justSynced) return null;

  // Hide banner when offline with nothing pending
  if (!isOnline && pendingCount === 0) return null;

  return (
    <div style={{
      background: isOnline ? "#0a1a0a" : "#1a0a00",
      border: `1px solid ${isOnline ? "#2ecc71" : "#f6a809"}`,
      borderRadius: "10px",
      padding: "12px 16px",
      margin: "0 0 16px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    }}>
      <span style={{ fontSize: "18px" }}>
        {syncing ? "🔄" : justSynced ? "✅" : isOnline ? "✅" : "📵"}
      </span>
      <div>
        <p style={{
          margin: 0, fontSize: "13px", fontWeight: "700",
          color: isOnline ? "#2ecc71" : "#f6a809",
        }}>
          {syncing
            ? "Syncing your offline data..."
            : justSynced
            ? "Sync complete"
            : isOnline && pendingCount > 0
            ? `${pendingCount} record${pendingCount !== 1 ? "s" : ""} pending sync`
            : "You are offline"}
        </p>
        <p style={{
          margin: "2px 0 0", fontSize: "12px",
          color: isOnline ? "#3a6a3a" : "#8a6a3a",
        }}>
          {syncing
            ? "Please wait while we upload your offline records"
            : justSynced
            ? "All records uploaded successfully"
            : isOnline && pendingCount > 0
            ? "Reconnected — tap to sync now"
            : "Sales and products saved locally. Will sync when you reconnect."}
        </p>
      </div>
      {isOnline && pendingCount > 0 && !syncing && (
        <button
          onClick={runSync}
          style={{
            marginLeft: "auto",
            background: "#2ecc71",
            border: "none",
            borderRadius: "6px",
            padding: "6px 12px",
            color: "#0a0f0f",
            fontSize: "12px",
            fontWeight: "700",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          Sync Now
        </button>
      )}
    </div>
  );
}